import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthContext';

interface ForcePasswordChangeGuardProps {
  readonly children: ReactNode;
}

/**
 * Plan 06-03 AUTH-105 — UX-side enforcement of the force-change-password flow.
 *
 * When the authenticated user has `passwordChangeRequired === true`, every authed
 * route except `/force-change-password` is short-circuited to a redirect at
 * `/force-change-password`. This is the CLIENT-SIDE complement of the backend's
 * global `RequireNotForceChangeAttribute` filter (Plan 06-02): the backend is the
 * authoritative enforcement (it 403s every other endpoint when the JWT carries
 * `force_password_change=true`), and this guard is purely so the user lands on
 * the change-password screen instead of seeing a blank dashboard littered with
 * 403 errors.
 */
export function ForcePasswordChangeGuard({ children }: ForcePasswordChangeGuardProps): JSX.Element {
  const { user } = useAuth();
  const location = useLocation();

  // REVIEW FF-026: normalise the pathname before comparing — trailing slash +
  // case variants should not bypass the guard.
  const normalisedPath = location.pathname.toLowerCase().replace(/\/+$/, '');

  if (user?.passwordChangeRequired === true && normalisedPath !== '/force-change-password') {
    return <Navigate to="/force-change-password" replace />;
  }

  return <>{children}</>;
}
